import { useState, useEffect, useCallback, useMemo } from "react";
import { 
  Calendar, 
  CalendarDays, 
  Mail, 
  CheckCircle,
  XCircle,
  Clock,
  Trash2,
  Eye,
  Plus,
  Edit,
  Users,
  User,
  LayoutDashboard,
  Upload,
  Image as ImageIcon,
  Images,
  Ticket,
  Building,
  Settings,
  Key
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function StaffMyShifts({ credentials, staffInfo }) {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);

  const headers = useMemo(() => {
    const auth = btoa(`${credentials.username}:${credentials.password}`);
    return { Authorization: `Basic ${auth}` };
  }, [credentials.username, credentials.password]);

  // Format date helper
  const formatDateStr = (date) => {
    if (!date) return null;
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  // Fetch shifts
  const fetchShifts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/staff/my-shifts`, { headers });
      setShifts(response.data);
    } catch (error) {
      console.error("Error fetching shifts:", error);
      toast.error("Failed to load your shifts");
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    if (credentials.username && credentials.password) {
      fetchShifts();
    }
  }, [credentials.username, credentials.password, fetchShifts]);

  // Get shift dates for calendar highlighting
  const shiftDates = [...new Set(shifts.map(s => s.shift_date))].map(d => new Date(d + 'T12:00:00'));

  // Get shifts for selected date
  const selectedDateStr = formatDateStr(selectedDate);
  const dayShifts = selectedDateStr ? shifts.filter(s => s.shift_date === selectedDateStr) : [];

  const formatDateDisplay = (d) => d?.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div data-testid="staff-my-shifts">
      <div className="flex items-center gap-4 mb-8">
        <div className="w-16 h-16 bg-[#3B82F6] rounded-full flex items-center justify-center text-white text-2xl font-bold">
          {staffInfo?.name?.charAt(0)?.toUpperCase() || 'S'}
        </div>
        <div>
          <h1 className="heading-lg">My Shifts</h1>
          <p className="text-[#78716C]">Welcome, {staffInfo?.name || credentials.username}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Calendar */}
        <Card className="card-heritage">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-[#3B82F6]" />
              Shift Calendar
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex justify-center">
              <CalendarPicker
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                month={currentMonth}
                onMonthChange={setCurrentMonth}
                modifiers={{
                  hasShift: shiftDates
                }}
                modifiersStyles={{
                  hasShift: {
                    backgroundColor: '#3B82F6',
                    color: 'white',
                    borderRadius: '50%'
                  }
                }}
                className="rounded-md border"
                data-testid="my-shifts-calendar"
              />
            </div>
            
            {selectedDate && (
              <div className="mt-6 p-4 bg-[#F5F5F0] rounded-lg">
                <h3 className="font-semibold text-[#1C1917] mb-3">{formatDateDisplay(selectedDate)}</h3>
                {dayShifts.length === 0 ? (
                  <p className="text-[#78716C]">No shift scheduled for this day</p>
                ) : (
                  <div className="space-y-2">
                    {dayShifts.map(shift => (
                      <div key={shift.id} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-[#E7E5E4]">
                        <Clock className="w-5 h-5 text-[#3B82F6]" />
                        <div>
                          <p className="font-medium">{shift.start_time}{shift.end_time ? ` - ${shift.end_time}` : ''}</p>
                          {shift.notes && <p className="text-sm text-[#78716C]">{shift.notes}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Shifts List */}
        <Card className="card-heritage">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#3B82F6]" />
              Upcoming Shifts
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="animate-pulse space-y-3">
                <div className="h-16 bg-[#E7E5E4] rounded"></div>
                <div className="h-16 bg-[#E7E5E4] rounded"></div>
                <div className="h-16 bg-[#E7E5E4] rounded"></div>
              </div>
            ) : shifts.length === 0 ? (
              <div className="text-center py-12">
                <CalendarDays className="w-16 h-16 text-[#D6D3D1] mx-auto mb-4" />
                <p className="text-[#78716C]">No upcoming shifts scheduled</p>
                <p className="text-sm text-[#A8A29E] mt-1">Check back later for new shifts</p>
              </div>
            ) : (
              <div className="space-y-3">
                {shifts.map(shift => {
                  const shiftDate = new Date(shift.shift_date + 'T12:00:00');
                  const isToday = formatDateStr(new Date()) === shift.shift_date;
                  const isTomorrow = formatDateStr(new Date(Date.now() + 86400000)) === shift.shift_date;
                  
                  return (
                    <div 
                      key={shift.id}
                      className={`p-4 rounded-lg border ${
                        isToday ? 'bg-[#DBEAFE] border-[#3B82F6]' : 
                        isTomorrow ? 'bg-[#FEF3C7] border-[#D97706]' : 
                        'bg-white border-[#E7E5E4]'
                      }`}
                      data-testid={`shift-${shift.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            {isToday && (
                              <span className="text-xs bg-[#3B82F6] text-white px-2 py-0.5 rounded font-medium">TODAY</span>
                            )}
                            {isTomorrow && (
                              <span className="text-xs bg-[#D97706] text-white px-2 py-0.5 rounded font-medium">TOMORROW</span>
                            )}
                          </div>
                          <p className="font-semibold text-[#1C1917] mt-1">
                            {shiftDate.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-mono text-lg font-bold text-[#1C1917]">{shift.start_time}</p>
                          {shift.end_time && (
                            <p className="text-sm text-[#78716C]">until {shift.end_time}</p>
                          )}
                        </div>
                      </div>
                      {shift.notes && (
                        <p className="text-sm text-[#78716C] mt-2 pt-2 border-t border-[#E7E5E4]">
                          {shift.notes}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Contact Info */}
      <Card className="card-heritage mt-6">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 text-[#78716C]">
            <Mail className="w-5 h-5" />
            <p>If you have any questions about your shifts, please contact the hall administrator.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

